import {
  type ExpenseInput,
  expenseProblem,
  localDay,
  MAX_EXPENSE_FILES,
  newId,
} from '@quickreport/domain';
import { type LocalDb, queueChange, type Tx } from '@quickreport/sync';
import { recordAudit } from './audit';
import { requirePermission } from './permissions';
import { WorkflowError, type WorkspaceCtx } from './reports';

export interface ExpenseRow {
  id: string;
  spent_on: string;
  amount_paise: number;
  category: string;
  vendor: string | null;
  method: string;
  note: string | null;
  file_ids: string[];
  updated_at: string;
}

function clean(e: ExpenseInput): ExpenseInput {
  return {
    ...e,
    category: e.category.trim().slice(0, 60),
    ...(e.vendor?.trim() ? { vendor: e.vendor.trim().slice(0, 120) } : {}),
    ...(e.note?.trim() ? { note: e.note.trim().slice(0, 500) } : {}),
    fileIds: [...new Set(e.fileIds ?? [])],
  };
}

function need(ctx: WorkspaceCtx) {
  requirePermission(ctx, 'manage_expenses', 'Your role cannot record expenses.');
}

export async function addExpense(
  db: LocalDb,
  ctx: WorkspaceCtx,
  input: ExpenseInput,
): Promise<string> {
  need(ctx);
  const e = clean(input);
  const problem = expenseProblem(e);
  if (problem) throw new WorkflowError(problem);
  const id = newId();
  await db.transaction(async (tx) => {
    await tx.query(
      `insert into expenses (id, org_id, spent_on, amount_paise, category, vendor, method, note, file_ids)
       values ($1, $2, $3::date, $4, $5, $6, $7, $8, $9::jsonb)`,
      [
        id,
        ctx.orgId,
        e.spentOn,
        e.amountPaise,
        e.category,
        e.vendor ?? null,
        e.method ?? 'cash',
        e.note ?? null,
        JSON.stringify(e.fileIds),
      ],
    );
    await queueChange(tx, 'expenses', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'expense',
      entityId: id,
      action: 'expense_added',
      after: { amountPaise: e.amountPaise, category: e.category },
    });
  });
  return id;
}

export async function updateExpense(
  db: LocalDb,
  ctx: WorkspaceCtx,
  id: string,
  input: ExpenseInput,
): Promise<void> {
  need(ctx);
  const e = clean(input);
  const problem = expenseProblem(e);
  if (problem) throw new WorkflowError(problem);
  await db.transaction(async (tx) => {
    const cur = await tx.query<{ amount_paise: number; category: string }>(
      'select amount_paise, category from expenses where id = $1 and deleted_at is null',
      [id],
    );
    if (!cur.rows[0]) throw new WorkflowError('That expense no longer exists.');
    await tx.query(
      `update expenses set spent_on = $2::date, amount_paise = $3, category = $4, vendor = $5,
         method = $6, note = $7, file_ids = $8::jsonb, updated_at = now() where id = $1`,
      [
        id,
        e.spentOn,
        e.amountPaise,
        e.category,
        e.vendor ?? null,
        e.method ?? 'cash',
        e.note ?? null,
        JSON.stringify(e.fileIds),
      ],
    );
    await queueChange(tx, 'expenses', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'expense',
      entityId: id,
      action: 'expense_changed',
      before: { amountPaise: Number(cur.rows[0].amount_paise), category: cur.rows[0].category },
      after: { amountPaise: e.amountPaise, category: e.category },
    });
  });
}

/** Removing an expense keeps it in the audit trail. */
export async function deleteExpense(db: LocalDb, ctx: WorkspaceCtx, id: string): Promise<void> {
  need(ctx);
  await db.transaction(async (tx) => {
    const cur = await tx.query<{ amount_paise: number; category: string }>(
      'select amount_paise, category from expenses where id = $1 and deleted_at is null',
      [id],
    );
    if (!cur.rows[0]) return;
    await tx.query('update expenses set deleted_at = now(), updated_at = now() where id = $1', [
      id,
    ]);
    await queueChange(tx, 'expenses', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'expense',
      entityId: id,
      action: 'expense_removed',
      before: { amountPaise: Number(cur.rows[0].amount_paise), category: cur.rows[0].category },
    });
  });
}

export interface ExpenseFilters {
  q?: string;
  category?: string;
  /** Inclusive days, YYYY-MM-DD. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export const EXPENSE_PAGE = 50;

function where(orgId: string, f: ExpenseFilters) {
  const params: unknown[] = [orgId];
  const w = ['org_id = $1', 'deleted_at is null'];
  for (const word of (f.q ?? '').toLowerCase().split(/\s+/).filter(Boolean)) {
    params.push(`%${word}%`);
    w.push(
      `lower(coalesce(vendor, '') || ' ' || coalesce(note, '') || ' ' || category) like $${params.length}`,
    );
  }
  if (f.category) {
    params.push(f.category);
    w.push(`category = $${params.length}`);
  }
  if (f.from) {
    params.push(f.from);
    w.push(`spent_on >= $${params.length}::date`);
  }
  if (f.to) {
    params.push(f.to);
    w.push(`spent_on <= $${params.length}::date`);
  }
  return { where: w.join(' and '), params };
}

export function expenseListQuery(orgId: string, f: ExpenseFilters = {}) {
  const { where: w, params } = where(orgId, f);
  const size = Math.min(Math.max(Math.floor(f.pageSize ?? EXPENSE_PAGE), 1), 500);
  const offset = (Math.max(Math.floor(f.page ?? 1), 1) - 1) * size;
  return {
    sql: `select id, spent_on::text as spent_on, amount_paise, category, vendor, method, note, file_ids, updated_at
            from expenses where ${w}
           order by spent_on desc, created_at desc, id limit ${size} offset ${offset}`,
    params,
  };
}

/** How many expenses match, and what they add up to, ignoring the page. */
export function expenseTotalQuery(orgId: string, f: ExpenseFilters = {}) {
  const { where: w, params } = where(orgId, f);
  return {
    sql: `select count(*)::int as n, coalesce(sum(amount_paise), 0)::float8 as total from expenses where ${w}`,
    params,
  };
}

/** The categories to offer: the usual ones, then any this lab has typed in. */
export async function expenseCategoriesInUse(
  db: Pick<LocalDb, 'query'> | Tx,
  orgId: string,
): Promise<string[]> {
  const r = await db.query<{ category: string }>(
    'select distinct category from expenses where org_id = $1 and deleted_at is null order by category',
    [orgId],
  );
  return r.rows.map((x) => x.category);
}

export interface ExpenseSummary {
  /** First day of this month, YYYY-MM-DD. */
  monthStart: string;
  thisMonthPaise: number;
  lastMonthPaise: number;
  byCategory: { category: string; paise: number }[];
}

/** This month's spending against last month's, and where it went. */
export async function expenseSummary(
  db: Pick<LocalDb, 'query'> | Tx,
  orgId: string,
  today: string = localDay(),
): Promise<ExpenseSummary> {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const p = (n: number) => String(n).padStart(2, '0');
  const monthStart = `${y}-${p(m)}-01`;
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${p(m + 1)}-01`;
  const lastStart = m === 1 ? `${y - 1}-12-01` : `${y}-${p(m - 1)}-01`;
  const sums = await db.query<{ this_month: number; last_month: number }>(
    `select coalesce(sum(amount_paise) filter (where spent_on >= $2::date and spent_on < $3::date), 0)::float8 as this_month,
            coalesce(sum(amount_paise) filter (where spent_on >= $4::date and spent_on < $2::date), 0)::float8 as last_month
       from expenses where org_id = $1 and deleted_at is null`,
    [orgId, monthStart, next, lastStart],
  );
  const cats = await db.query<{ category: string; paise: number }>(
    `select category, sum(amount_paise)::float8 as paise from expenses
      where org_id = $1 and deleted_at is null and spent_on >= $2::date and spent_on < $3::date
      group by category order by 2 desc`,
    [orgId, monthStart, next],
  );
  return {
    monthStart,
    thisMonthPaise: Number(sums.rows[0]?.this_month ?? 0),
    lastMonthPaise: Number(sums.rows[0]?.last_month ?? 0),
    byCategory: cats.rows.map((c) => ({ category: c.category, paise: Number(c.paise) })),
  };
}

export { MAX_EXPENSE_FILES };
